import type { TelegramClient } from "telegram";
import { UPSERT_CHUNK, type DialogUpsert, type PeerType } from "@tg/shared";
import { api } from "../lib/api";

export interface EnumProgress {
  seen: number;
  currentTitle: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDialog(d: any): DialogUpsert | null {
  const e = d?.entity;
  if (!e) return null;
  const cls: string = e.className;
  let peerType: PeerType;
  let accessHash: string | null = null;
  let username: string | null = null;
  let isCreator = false;
  let canSend = true;
  let reason: string | null = null;

  if (cls === "User") {
    peerType = e.bot ? "bot" : "user";
    accessHash = e.accessHash ? e.accessHash.toString() : null;
    username = e.username ?? null;
    if (e.deleted) {
      canSend = false;
      reason = "deleted";
    }
  } else if (cls === "Chat") {
    peerType = "group";
    isCreator = !!e.creator;
    if (e.left || e.kicked || e.deactivated) {
      canSend = false;
      reason = e.left ? "left" : e.kicked ? "kicked" : "deactivated";
    } else if (e.defaultBannedRights?.sendMessages) {
      canSend = false;
      reason = "banned";
    }
  } else if (cls === "Channel") {
    peerType = e.megagroup || e.gigagroup ? "supergroup" : "channel";
    accessHash = e.accessHash ? e.accessHash.toString() : null;
    username = e.username ?? null;
    isCreator = !!e.creator;
    if (e.left) {
      canSend = false;
      reason = "left";
    } else if (peerType === "channel" && !e.creator && !e.adminRights?.postMessages) {
      canSend = false;
      reason = "no_post_rights";
    } else if (e.defaultBannedRights?.sendMessages && !e.creator && !e.adminRights) {
      canSend = false;
      reason = "banned";
    }
  } else {
    return null;
  }

  const peerId = e.id?.toString();
  if (!peerId) return null;
  const title: string =
    d.title || e.title || [e.firstName, e.lastName].filter(Boolean).join(" ") || "(sin título)";

  return {
    peerId,
    accessHash,
    peerType,
    title,
    username,
    canSend,
    canSendReason: reason,
    isCreator,
    archived: !!d.archived,
  };
}

export async function enumerateDialogs(
  client: TelegramClient,
  onProgress: (p: EnumProgress) => void,
  signal?: AbortSignal,
): Promise<{ active: number; deactivated: number }> {
  const { marker } = await api.refreshBegin();
  let batch: DialogUpsert[] = [];
  let seen = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for await (const d of client.iterDialogs({ archived: false } as any)) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    const row = mapDialog(d);
    if (!row) continue;
    batch.push(row);
    seen++;
    onProgress({ seen, currentTitle: row.title });
    if (batch.length >= UPSERT_CHUNK) {
      await api.bulkUpsert({ marker, rows: batch });
      batch = [];
    }
  }
  if (batch.length) await api.bulkUpsert({ marker, rows: batch });
  return api.refreshCommit(marker);
}
